import React, { useEffect, useState } from 'react';
import './banner.scss';

const Banner = () => {
  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [expandedCards, setExpandedCards] = useState({});

  // ===== Coingecko Data (MRKTS + GEMS) =====
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);

    const fetchData = async () => {
      try {
        if (!data.gems) setLoadingData(true); // only show loader if no data yet

        const response = await fetch(
          "https://pro-api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=gems-vip,data-ownership-protocol-2, luck-3, rain",
          {
            headers: {
              'x-cg-pro-api-key': 'CG-AMT6oz8K7Qpx7qESamW9hKZu',
            }
          }
        );
        const result = await response.json();

        const gemsData = result.find(token => token.id === 'gems-vip');
        const dopData = result.find(token => token.id === 'data-ownership-protocol-2');
        const luckData = result.find(token => token.id === 'luck-3');
        const rainData = result.find(token => token.id === 'rain');


        setData({
          gems: {
            price: gemsData?.current_price,
            circulating_supply: gemsData?.circulating_supply,
            market_cap: gemsData?.market_cap,
            volume: 491573033 * gemsData?.current_price,
            newvolume: gemsData?.total_volume
          },
          dop: {
            price: dopData?.current_price,
            circulating_supply: dopData?.circulating_supply,
            market_cap: dopData?.market_cap,
            volume: dopData?.total_volume,
            estvolume: 130000000 * dopData?.current_price
          },
          luck: {
            price: luckData?.current_price,
            volume: luckData?.total_volume,
          },
          rain: {
            price: rainData?.current_price,
            volume: rainData?.total_volume,
          }
        });
      } catch (error) {
        console.error("Error fetching token data:", error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 25000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

 

  // ===== Helpers =====
  const format = (val) => {
    if (val == null) return 'N/A';

    let num = typeof val === 'string'
      ? parseFloat(val.replace(/,/g, ''))
      : Number(val);

    if (isNaN(num)) return 'N/A';

    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const toggleCard = (name) => {
    setExpandedCards(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const tokenList = {
    mrkts: [
      {
        name: 'GEMS',
        amount: "491,573,033",
        price: data.gems?.price
      },
      { name: 'RAIN', amount: '747,500,000,000', price: data.rain?.price, total: data.rain?.price * 747500000000 },
    ],
    gems: [
      { name: 'RAIN', amount: '115,000,000,000', price: data.rain?.price, total: data.rain?.price * 115000000000 },
      {
        name: 'DOP',
        amount: "130,000,000",
        price: data.dop?.price,
        total: data.dop?.estvolume,
        note: '--'
      },
      { name: 'LUCK', amount: '150,000,000', price: data.luck?.price, total: data.luck?.price * 150000000, note: '*Low liquidity' },
      { name: 'Incentiv', amount: '384,615,385', price: 0.0026, total: 1000000, note: '*Listing price' },
      { name: 'Leia', amount: '81,000,000', price: 0.00, total: 5900 },
      { name: 'OlympusAI', amount: '10,000,000', price: 0.01, total: 1000000, note: '*Listing price' }
    ]
  };

  const calculateTotalEstimatedValue = () => {
    const staticTokens = tokenList.gems.filter(token =>
      ['Incentiv', 'Leia', 'OlympusAI', 'RAIN'].includes(token.name)
    );

    const staticTotal = staticTokens.reduce((sum, token) => {
      return sum + (token.total || 0);
    }, 0);

    const gemsdata = data.gems?.volume || 0;
    const dopdata = data.dop?.estvolume || 0;
    const rainTotal = data.rain?.price * 747500000000;
    const luckTotal = data.luck?.price * 150000000;

    return staticTotal + gemsdata + dopdata + rainTotal + luckTotal;
  };

  // ===== Render Helpers =====
  const renderTable = (list) => (
    <table className="table">
      <thead>
        <tr>
          <th>Token</th>
          <th>AMOUNT (TOKENS)</th>
          <th>PRICE</th>
          <th>Total</th>
          <th>Note</th>
        </tr>
      </thead>
      <tbody>
        {list.map((token) => {
          const { name, amount, price, note, total: manualTotal } = token;
          const total = manualTotal ?? (price && amount ? price * parseFloat(amount.toString().replace(/,/g, '')) : null);
          return (
            <tr key={name}>
              <td>
                <div className="withimg">
                  <img src={`\\assets\\tokens\\${name.toLowerCase()}.svg`} alt={name} className='img-fluid' />
                  <h6>{name}</h6>
                </div>
              </td>
              <td>{format(amount)}</td>
              <td>{price !== null && price !== undefined ? `$${price}` : '--'}</td>
              <td>{total ? `$${format(total)}` : '--'}</td>
              <td>{note || '--'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderMobile = (list) => (
    <div className="mobile-data">
      {list.map((token) => {
        const { name, amount, price, note, total: manualTotal } = token;
        const total = manualTotal ?? (price && amount ? price * parseFloat(amount.toString().replace(/,/g, '')) : null);
        const isExpanded = expandedCards[name];

        const fields = [
          {
            label: 'Token',
            content: (
              <div className="withimg">
                <img src={`\\assets\\tokens\\${name.toLowerCase()}.svg`} alt={name} className='img-fluid' />
                <h6>{name}</h6>
              </div>
            )
          },
          { label: 'Total', content: total ? `$${format(total)}` : '--' },
          { label: 'AMOUNT (TOKENS)', content: format(amount) },
          { label: 'PRICE', content: price !== null && price !== undefined ? `$${price}` : '--' },
          { label: 'Note', content: note || '--' },
        ];

        return (
          <div className="single-data" key={name}>
            {fields.map((field, index) => (
              (isExpanded || index < 2) && (
                <div className="text" key={field.label}>
                  <p>{field.label}</p>
                  <h6>{field.content}</h6>
                </div>
              )
            ))}
            {fields.length > 2 && (
              <button className='btn-show' onClick={() => toggleCard(name)}>
                {isExpanded ? 'Show Less' : 'Show More'}{' '}
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d={isExpanded ? "M2 9L7 4L12 9" : "M2 5L7 10L12 5"}
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );

  // ===== UI =====
  return (
    <section className='main-banner'>
      <img src="\assets\banner\banner-bg.png" alt="banner" className='img-fluid banner-bg' />
      <div className="custom-container">
        <div className="main-content">
          <div className="left-side">
            <h1>MRKTS HOLDINGS</h1>
            <p>Overview of current treasury and token holdings across MRKTS.</p>
          </div>
          <div className="right-side">
            <div className="markets-total">
              <h6>MRKTS TOTAL ESTIMATED VALUE</h6>
              {(!data.gems?.price || !data.dop?.price || !data.rain?.price || !data.luck?.price)
                ? <h5>Loading...</h5>
                : <h5>${format(Math.round(calculateTotalEstimatedValue()))}</h5>}
              <p>
                Volume (24h): {
                  (!data.gems?.newvolume || !data.dop?.volume)
                    ? "Loading..."
                    : `$${new Intl.NumberFormat('en', {
                      notation: 'compact',
                      compactDisplay: 'short',
                    }).format((data.gems.newvolume || 0) + (data.dop.volume || 0) + (data.luck.volume || 0) + (data.rain.volume || 0))}`
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bottom-content">
          {['mrkts', 'gems'].map(section => (
            <div className="common-table" key={section}>
              <div className="main-heading">
                <h6>{section === 'mrkts' ? 'MRKTS Holdings' : 'Gems Holdings'}</h6>
                <p>{section === 'mrkts'
                  ? 'This section provides an overview of assets currently held under MRKTS.'
                  : 'This section provides an overview of assets currently held under Gems ecosystem.'}
                </p>
              </div>
              {isMobile
                ? renderMobile(tokenList[section])
                : <div className="table-responsive">{renderTable(tokenList[section])}</div>
              }
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Banner;
